import { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  DeductionChip,
  PriceRow,
  PriceRowVariant,
} from "@/components/Layout/PriceTable/PriceRow";
import { getCurrency } from "@/utils/translationHelpers";

const captionClassName = "text-body-xs font-regular leading-md";

/** A discount name followed by its red "(until …)" end date, if it has one. */
export const DiscountLabel = ({
  name,
  endDate,
}: {
  name: ReactNode;
  endDate?: Date;
}) => {
  const { t } = useTranslation();

  return (
    <>
      {name}
      {endDate && (
        <Trans
          t={t}
          i18nKey="Onboarding.sections.checkout.summary.until_date"
          values={{ date: endDate.toLocaleDateString("de-CH") }}
          components={[
            <span key="until_date" className="text-foreground-error" />,
          ]}
        />
      )}
    </>
  );
};

/** A discount or credit row: the label and a mint "- CHF X" chip. */
export const DeductionRow = ({
  label,
  amount,
  dataTestid,
  variant,
  withDivider,
}: {
  label: ReactNode;
  amount: number;
  dataTestid?: string;
  variant?: PriceRowVariant;
  withDivider?: boolean;
}) => (
  <PriceRow
    variant={variant}
    withDivider={withDivider}
    label={label}
    value={
      <span data-testid={dataTestid}>
        <DeductionChip amount={amount} />
      </span>
    }
  />
);

/**
 * The bold total closing a due-now card, with "incl. VAT" under its label.
 * A negative total (credit exceeding the price) shows as zero.
 */
export const DueNowTotalRow = ({
  label,
  amount,
  currency,
  caption,
  dataTestid,
  withTopDivider = true,
}: {
  label: ReactNode;
  amount: number;
  currency: string;
  /** Shown under the amount, e.g. "once" or "today". */
  caption?: ReactNode;
  dataTestid?: string;
  withTopDivider?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <PriceRow
      withTopDivider={withTopDivider}
      variant="strong"
      label={
        <>
          <span>{label}</span>
          <br />
          <span className={captionClassName}>
            {t("Onboarding.sections.checkout.summary.taxIncluded")}
          </span>
        </>
      }
      value={
        <>
          <span data-testid={dataTestid}>
            {getCurrency(Math.max(amount, 0), currency)}
          </span>
          {caption && <span className={captionClassName}>{caption}</span>}
        </>
      }
    />
  );
};

/** The highlighted "Thereafter" row: what each following bill will cost. */
export const ThereafterRow = ({
  amount,
  currency,
  date,
  billingPeriod,
  dataTestid,
}: {
  amount: number;
  currency: string;
  /** Shown under the label, e.g. the next bill date. */
  date?: ReactNode;
  billingPeriod?: ReactNode;
  dataTestid?: string;
}) => {
  const { t } = useTranslation();

  return (
    <PriceRow
      withBackdrop
      variant="strong"
      label={
        <>
          <span>{t("Common.checkout.thereafter")}</span>
          {date && (
            <>
              <br />
              <span className={captionClassName}>{date}</span>
            </>
          )}
        </>
      }
      value={
        <>
          <span data-testid={dataTestid}>{getCurrency(amount, currency)}</span>
          {billingPeriod && (
            <span className={captionClassName}>{billingPeriod}</span>
          )}
        </>
      }
    />
  );
};
